import type { AppointmentTypeBehavior } from "@/hooks/useClinicAppointmentTypes";
import type { Json } from "@/integrations/supabase/types";

export const FALLBACK_APPOINTMENT_DURATION_MINUTES = 60;

export const APPOINTMENT_DURATION_BEHAVIOR_KEYS = [
  "padrao",
  "retorno",
  "aplicacao",
  "banho_tosa",
] as const satisfies AppointmentTypeBehavior[];

export type AppointmentDurationBehaviorKey = (typeof APPOINTMENT_DURATION_BEHAVIOR_KEYS)[number];

export function addMinutesToTimeString(time: string, minutes: number): string {
  if (!time || !time.includes(":")) return "";
  const [hRaw, mRaw] = time.split(":");
  const hours = parseInt(hRaw, 10);
  const mins = parseInt(mRaw, 10);
  if (Number.isNaN(hours) || Number.isNaN(mins)) return "";
  const total = hours * 60 + mins + minutes;
  const wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const endH = Math.floor(wrapped / 60);
  const endM = wrapped % 60;
  return `${endH.toString().padStart(2, "0")}:${endM.toString().padStart(2, "0")}`;
}

export function parseDurationMapFromJson(
  json: Json | null | undefined,
): Partial<Record<AppointmentDurationBehaviorKey, number>> {
  if (json === null || json === undefined) return {};
  if (typeof json !== "object" || Array.isArray(json)) return {};
  const out: Partial<Record<AppointmentDurationBehaviorKey, number>> = {};
  for (const key of APPOINTMENT_DURATION_BEHAVIOR_KEYS) {
    const raw = (json as Record<string, unknown>)[key];
    if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
      out[key] = Math.floor(raw);
    }
  }
  return out;
}

export function resolveDefaultDurationMinutes(
  behavior: string | null | undefined,
  map: Partial<Record<AppointmentDurationBehaviorKey, number>>,
): number {
  const key = behavior as AppointmentDurationBehaviorKey | undefined;
  if (key && key in map) {
    const fromMap = map[key];
    if (typeof fromMap === "number" && fromMap > 0) {
      return Math.min(1440, Math.floor(fromMap));
    }
  }
  return FALLBACK_APPOINTMENT_DURATION_MINUTES;
}
